import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Download, ArrowUpDown, ArrowUp, ArrowDown, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { useCsrCohortReport, useCsrCohortComparison } from '@/hooks/useCsrCohortReport';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { formatRate } from '@/utils/dateFieldStandards';
import { CsrKpiCards, type CsrKpiDrill } from './CsrKpiCards';
import { QualifiedFunnel } from './QualifiedFunnel';
import { JunkLeadsSheet } from './JunkLeadsSheet';
import { CohortLeadsSheet } from './CohortLeadsSheet';
import { ClassBadge, JobStatusBadge } from './csrBadges';
import { LossReasonsChart } from './LossReasonsChart';
import { FollowUpRecoveryPanel } from './FollowUpRecoveryPanel';
import { HeatmapChart } from './HeatmapChart';
import type { CsrCohortLeadRow, CsrCohortStats, CsrLeaderboardRow } from '@/types/reports';

/**
 * CSR report. Every number on this page starts from leads.created_at in the
 * selected range and follows that cohort forward (see useCsrCohortReport).
 * Revenue, completion and answer rate are not CSR-controlled and live on the
 * Overall / Technicians tabs.
 */

interface CsrReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

type SortKey = 'csr_name' | 'leads' | 'booked' | 'book_rate' | 'same_call_rate' | 'held_rate' | 'lost_csr' | 'sla_rate';
type ColumnKey = SortKey | 'price_quoted' | 'script_score';

interface Column {
  key: ColumnKey;
  label: string;
  sortable: boolean;
  className?: string;
  hint?: string;
}

const COLUMNS: Column[] = [
  { key: 'csr_name', label: 'CSR', sortable: true, className: 'text-left' },
  { key: 'leads', label: 'Leads', sortable: true, className: 'text-center', hint: 'Gradable leads: received minus excluded and lost-to-ops' },
  { key: 'booked', label: 'Booked', sortable: true, className: 'text-center', hint: 'A job exists for the lead' },
  { key: 'book_rate', label: 'Book %', sortable: true, className: 'text-center', hint: 'Booked ÷ Leads' },
  { key: 'same_call_rate', label: 'Same-call %', sortable: true, className: 'text-center', hint: 'Job created within 5 min of the end of the first inbound call (or of lead creation when there was no call) ÷ Booked' },
  { key: 'held_rate', label: 'Held %', sortable: true, className: 'text-center', hint: 'Completed, or cancelled by the tech on site ÷ completed + cancelled. Jobs not yet decided are left out.' },
  { key: 'lost_csr', label: 'Lost', sortable: true, className: 'text-center', hint: 'Lost for a CSR-controllable reason: not ready, price, went elsewhere, other' },
  { key: 'price_quoted', label: 'Price quoted', sortable: false, className: 'text-center', hint: 'Needs AI call scoring (separate ticket)' },
  { key: 'script_score', label: 'Script score', sortable: false, className: 'text-center', hint: 'Needs AI call scoring (separate ticket)' },
  { key: 'sla_rate', label: 'SLA 5 min', sortable: true, className: 'text-center', hint: 'Web-form and unanswered-call leads contacted (call or SMS) within 5 min of creation. Live-answered calls are not in the denominator.' },
];

// ClassBadge / JobStatusBadge live in ./csrBadges so the KPI drill-down sheet
// can render the same labels without importing this file (which imports it).

/** "n/m" fraction shown under a rate cell */
function Fraction({ n, d }: { n: number; d: number }) {
  return <div className="text-[10px] text-muted-foreground leading-tight">{n}/{d}</div>;
}

/** The metric cells shared by CSR rows and the TOTAL footer (everything after the name column). */
function StatCells({ stats }: { stats: CsrCohortStats }) {
  const cell = 'px-2 py-3 text-center';
  return (
    <>
      <td className={cell}>
        <div className="font-medium">{stats.leads}</div>
        {stats.cohort !== stats.leads && (
          <div
            className="text-[10px] font-normal text-muted-foreground mt-0.5"
            title={`${stats.cohort} received: ${stats.excluded} excluded, ${stats.lost_ops} lost to ops`}
          >
            {stats.cohort} received
          </div>
        )}
      </td>
      <td className={cell}>{stats.booked}</td>
      <td className={cell}>{formatRate(stats.book_rate)}</td>
      <td className={cell}>
        <div>{formatRate(stats.same_call_rate)}</div>
        {stats.booked > 0 && <Fraction n={stats.same_call_count} d={stats.booked} />}
      </td>
      <td className={cell}>
        <div>{formatRate(stats.held_rate)}</div>
        {stats.held_denominator > 0 && <Fraction n={stats.held_numerator} d={stats.held_denominator} />}
      </td>
      <td className={cell}>{stats.lost_csr}</td>
      <td className={`${cell} text-muted-foreground`}>–</td>
      <td className={`${cell} text-muted-foreground`}>–</td>
      <td className={cell}>
        <div>{formatRate(stats.sla_rate)}</div>
        {stats.sla_applicable_count > 0 && <Fraction n={stats.sla_met_count} d={stats.sla_applicable_count} />}
      </td>
    </>
  );
}

interface CsrRowWithLeadsProps {
  csr: CsrLeaderboardRow;
  isExpanded: boolean;
  onToggle: () => void;
  onLeadClick: (leadId: string) => void;
}

function CsrRowWithLeads({ csr, isExpanded, onToggle, onLeadClick }: CsrRowWithLeadsProps) {
  const unattributed = csr.csr_id === null;
  return (
    <>
      <tr
        className={`border-b hover:bg-muted/30 cursor-pointer ${unattributed ? 'text-muted-foreground italic' : ''}`}
        onClick={onToggle}
      >
        <td className="px-2 py-3 font-medium">
          <div
            className="flex items-center gap-1"
            title={unattributed ? 'Leads with no booked-by, assigned CSR, or inbound-call agent' : undefined}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <span>{csr.csr_name}</span>
          </div>
        </td>
        <StatCells stats={csr} />
      </tr>
      {isExpanded && (
        csr.leadRows.length > 0 ? (
          csr.leadRows.map(lead => (
            <tr
              key={lead.lead_id}
              className="border-b bg-muted/20 text-muted-foreground hover:bg-muted/40 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onLeadClick(lead.lead_id);
              }}
            >
              <td className="px-2 py-2 pl-8 text-sm">
                <span className="font-mono">{lead.lead_number || lead.lead_id.slice(0, 8)}</span>
              </td>
              <td className="px-2 py-2 text-sm text-center"><ClassBadge cls={lead.class} /></td>
              <td colSpan={COLUMNS.length - 2} className="px-2 py-2 text-sm">
                <div className="flex items-center gap-3">
                  {lead.job_number ? (
                    <>
                      <span className="font-mono">{lead.job_number}</span>
                      <JobStatusBadge status={lead.job_status} />
                    </>
                  ) : lead.class === 'lost_csr' && lead.lost_reason ? (
                    <span className="truncate max-w-[420px]" title={lead.lost_reason}>{lead.lost_reason}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={COLUMNS.length} className="px-2 py-3 pl-8 text-sm text-muted-foreground text-center">
              No gradable leads
            </td>
          </tr>
        )
      )}
    </>
  );
}

const csvCell = (v: string | number | null | undefined): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const CsrReport = ({ startDate, endDate, timezone: propTimezone, compareEnabled = false }: CsrReportProps) => {
  const { timezone: contextTimezone, formatDate } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const navigate = useNavigate();
  const { data, isLoading, error } = useCsrCohortReport({ startDate, endDate, timezone });

  // Comparison data (deferred: only when enabled and main data loaded).
  // Must be called before any early returns to respect React's rules of hooks.
  const comparison = useCsrCohortComparison(startDate, endDate, timezone, !isLoading && !!data && compareEnabled);

  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'book_rate',
    direction: 'desc',
  });
  const [expandedCsrs, setExpandedCsrs] = useState<Set<string>>(new Set());
  const [excludedSheetOpen, setExcludedSheetOpen] = useState(false);
  /** Which KPI tile the user clicked; null = no drill-down open. */
  const [kpiDrill, setKpiDrill] = useState<CsrKpiDrill | null>(null);

  const toggleCsr = (key: string) => {
    setExpandedCsrs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortedRows = useMemo(() => {
    if (!data?.leaderboard) return [];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...data.leaderboard].sort((a, b) => {
      // Unattributed always sits at the bottom
      if (a.csr_id === null) return 1;
      if (b.csr_id === null) return -1;
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
      // null rates sort last regardless of direction
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return ((av as number) - (bv as number)) * dir;
    });
  }, [data?.leaderboard, sortConfig]);

  const excludedRows = useMemo<CsrCohortLeadRow[]>(
    () => (data?.rows ?? []).filter(r => r.class === 'excluded'),
    [data?.rows],
  );

  // KPI tile → the leads that number is made of. Everything comes from the
  // rows already loaded for the page, so a drill-down costs no extra query.
  const drillDown = useMemo(() => {
    if (!kpiDrill) return null;
    const all = data?.rows ?? [];
    const gradable = all.filter(r => r.class === 'booked' || r.class === 'lost_csr' || r.class === 'open');
    switch (kpiDrill) {
      case 'gradable':
        return {
          title: 'Gradable leads',
          description: 'Leads created in this range that the CSR could influence. Excluded leads and leads lost because no tech was available are left out. This is the denominator for every rate on the page.',
          rows: gradable,
        };
      case 'booked':
        return {
          title: 'Booked leads',
          description: 'Gradable leads that became a booked job.',
          rows: gradable.filter(r => r.class === 'booked'),
        };
      case 'book_rate':
        return {
          title: 'Book rate — gradable leads',
          description: 'Booked divided by gradable leads. Every gradable lead is listed here, so you can see which ones booked and which did not.',
          rows: gradable,
        };
      case 'held':
        return {
          title: 'Held — decided jobs',
          description: 'Booked jobs that have been completed or cancelled. Jobs still scheduled, in progress, on hold or quote-sent are not decided yet and are not counted.',
          rows: all.filter(r => r.class === 'booked' && r.held !== null),
          showHeld: true,
        };
      case 'lost_csr':
        return {
          title: 'Lost by CSR',
          description: 'Gradable leads lost for a reason the CSR could influence: customer not ready, price, went elsewhere, other.',
          rows: gradable.filter(r => r.class === 'lost_csr'),
        };
      default:
        return null;
    }
  }, [kpiDrill, data?.rows]);

  const getSortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-50" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 inline" /> : <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  const handleExportCSV = () => {
    if (!data) return;
    const headers = [
      'CSR', 'Leads (gradable)', 'Received', 'Excluded', 'Lost to ops', 'Booked', 'Book %',
      'Same-call', 'Same-call %', 'Held', 'Held decided', 'Held %', 'Lost (CSR)', 'Open',
      'SLA met', 'SLA applicable', 'SLA 5min %',
    ];
    const toRow = (name: string, s: CsrCohortStats) => [
      name, s.leads, s.cohort, s.excluded, s.lost_ops, s.booked, formatRate(s.book_rate),
      s.same_call_count, formatRate(s.same_call_rate), s.held_numerator, s.held_denominator, formatRate(s.held_rate),
      s.lost_csr, s.open, s.sla_met_count, s.sla_applicable_count, formatRate(s.sla_rate),
    ];
    const rows = sortedRows.map(csr => toRow(csr.csr_name, csr));
    rows.push(toRow('TOTAL', data.totals));
    const csvContent = [headers, ...rows].map(row => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `csr-report-${formatDate(startDate, 'yyyy-MM-dd')}-to-${formatDate(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />CSR Performance</CardTitle></CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">Could not load the CSR report.</p>
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap">{(error as Error).message}</pre>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>CSR Performance</CardTitle></CardHeader>
        <CardContent><p className="text-muted-foreground text-center py-8">No leads in the selected date range.</p></CardContent>
      </Card>
    );
  }

  const comparisonProp = compareEnabled && comparison.data
    ? { totals: comparison.data.totals, label: comparison.period.label }
    : undefined;

  return (
    <div className="space-y-6">
      <CsrKpiCards
        totals={data.totals}
        comparison={comparisonProp}
        comparisonLoading={compareEnabled && comparison.isLoading}
        onDrillDown={setKpiDrill}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QualifiedFunnel
          cohortLeads={data.totals.cohort}
          excluded={data.totals.excluded}
          lostOps={data.totals.lost_ops}
          gradable={data.totals.leads}
          booked={data.totals.booked}
          heldNumerator={data.totals.held_numerator}
          heldDenominator={data.totals.held_denominator}
          onExcludedClick={() => setExcludedSheetOpen(true)}
        />
        <LossReasonsChart data={data.lostByCsr} title="Lost by CSR" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FollowUpRecoveryPanel data={data.followUpRecovery} />
        <LossReasonsChart data={data.excludedReasons} title="Excluded — not CSR-attributable" />
      </div>

      {/* CSR Leaderboard */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>CSR Leaderboard</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Leads created in the range, followed forward. A lead only ever counts in the period it was created.
              </p>
            </div>
            <Button onClick={handleExportCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  {COLUMNS.map(col => (
                    <th
                      key={col.key}
                      title={col.hint}
                      className={`px-2 py-3 font-semibold text-muted-foreground select-none whitespace-nowrap ${col.className || ''} ${col.sortable ? 'cursor-pointer hover:bg-muted/50' : 'cursor-help'}`}
                      onClick={col.sortable ? () => handleSort(col.key as SortKey) : undefined}
                    >
                      {col.label}{col.sortable && getSortIcon(col.key as SortKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 && (
                  <tr><td colSpan={COLUMNS.length} className="text-center py-6 text-muted-foreground">No CSR data for this period.</td></tr>
                )}
                {sortedRows.map(csr => {
                  const key = csr.csr_id ?? 'unattributed';
                  return (
                    <CsrRowWithLeads
                      key={key}
                      csr={csr}
                      isExpanded={expandedCsrs.has(key)}
                      onToggle={() => toggleCsr(key)}
                      onLeadClick={(leadId) => navigate(`/leads/${leadId}`)}
                    />
                  );
                })}
              </tbody>
              {sortedRows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-foreground/20 bg-muted/50 font-bold text-sm">
                    <td className="px-2 py-3">TOTAL</td>
                    <StatCells stats={data.totals} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      <HeatmapChart data={data.heatmap} />

      <JunkLeadsSheet
        open={excludedSheetOpen}
        onOpenChange={setExcludedSheetOpen}
        rows={excludedRows}
        timezone={timezone}
      />

      <CohortLeadsSheet
        open={drillDown !== null}
        onOpenChange={(next) => { if (!next) setKpiDrill(null); }}
        title={drillDown?.title ?? ''}
        description={drillDown?.description ?? ''}
        rows={drillDown?.rows ?? []}
        showHeld={drillDown?.showHeld}
        timezone={timezone}
      />
    </div>
  );
};
